'use client';

import { useEffect, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

// Counts down to the next December 25 in the visitor's own timezone, so the day
// count matches the calendar on their wall rather than a server clock.

function nextChristmas(now: Date): Date {
  const year = now.getFullYear();
  const thisYear = new Date(year, 11, 25, 0, 0, 0, 0);
  return now.getTime() <= thisYear.getTime() ? thisYear : new Date(year + 1, 11, 25, 0, 0, 0, 0);
}

type Remaining = { days: number; hours: number; minutes: number; seconds: number; target: Date };

function remainingUntil(target: Date, now: Date): Remaining {
  const diff = Math.max(0, target.getTime() - now.getTime());
  const seconds = Math.floor(diff / 1000);
  return {
    days: Math.floor(seconds / 86400),
    hours: Math.floor((seconds % 86400) / 3600),
    minutes: Math.floor((seconds % 3600) / 60),
    seconds: seconds % 60,
    target,
  };
}

export default function BrowserChristmasCountdown() {
  const usageReported = useRef(false);
  const [remaining, setRemaining] = useState<Remaining | null>(null);

  useEffect(() => {
    function tick() {
      const now = new Date();
      setRemaining(remainingUntil(nextChristmas(now), now));
    }
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!usageReported.current && remaining) {
      usageReported.current = true;
      void consumeToolUsage('days-until-christmas', '').catch(() => undefined);
    }
  }, [remaining]);

  const weekday = remaining
    ? remaining.target.toLocaleDateString('en-US', { weekday: 'long' })
    : '';

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Days until Christmas</h2>
          <p>
            A live countdown to the next December 25, counted in your own timezone so it matches
            your calendar.
          </p>
        </div>
        <span className="tool-live-badge">🎄</span>
      </div>

      <div className="tool-live-uploader" style={{ alignItems: 'center', gap: '0.75rem' }}>
        {remaining ? (
          <>
            <span
              aria-live="polite"
              style={{ fontSize: '4.5rem', fontWeight: 700, lineHeight: 1.05 }}
            >
              {remaining.days}
            </span>
            <strong style={{ fontSize: '1.2rem' }}>
              {remaining.days === 1 ? 'day to go' : 'days to go'}
            </strong>
            <span>
              plus {remaining.hours}h {remaining.minutes}m {remaining.seconds}s
            </span>
            <p className="tool-live-panel-note" style={{ textAlign: 'center' }}>
              Christmas Day {remaining.target.getFullYear()} falls on a {weekday}. The counter rolls
              over to the following year once the day has passed.
            </p>
          </>
        ) : (
          <p className="tool-live-panel-note">Starting the countdown…</p>
        )}
      </div>
    </article>
  );
}
